import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DataGrid } from '@mui/x-data-grid';
import OpenIcon from '@mui/icons-material/FolderOpen';
import UploadIcon from '@mui/icons-material/CloudUpload';
import AddIcon from '@mui/icons-material/LibraryAdd';
import ChevronIcon from '@mui/icons-material/ChevronRight';

import { 
  addProject,
  deleteProject,
  importProject,
  makeProjectCurrent,
  selectProjects
} from '../reducers/projectSlice';
import {
  setLists,
  getLists,
  selectLists,
  makeListCurrent,
} from '../reducers/listSlice';
import { addWells, setWells, selectWells } from '../reducers/wellSlice';

import { Button, Card, CardActions, CardContent, CircularProgress, ClickAwayListener, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, FormGroup, IconButton, InputBase, Paper, Switch, Tooltip, Typography } from '@mui/material';
import { makeStyles, useTheme } from "@mui/styles";
import '../styles/Projects.css';
import { Link } from 'react-router-dom';

import { AccessTime, CloudUpload, CopyAll, Delete, Edit, GetApp, Group, Launch, Search } from '@mui/icons-material';

import { formatUwi } from '../helpers/formatUwi';
import { colors } from '../helpers/colors';
import { selectOptions, setOptions } from '../reducers/optionsSlice';
import { selectTypeCurves } from '../reducers/typeCurveSlice';
import { EditProject } from '../components/EditProject';
import { useSnackbar } from 'notistack';
import { selectUser } from '../reducers/userSlice';

import uuid from 'react-uuid';
import { deleteEconomics, saveEconomics, selectEconomics, updateCurrentEconomics, updateEconomics } from '../reducers/economicsSlice';
import { selectFBA, setCurrentFBAId } from '../reducers/fbaSlice';
import { useSocket } from '../context/useSocket';
import { selectGroupWells } from '../reducers/groupWellSlice';

const useStyles = makeStyles(theme => ({
  root: {
    border: 0,
    color: 'text.primary',
    // height: '100%',
    height: '100vh',
    width: 'calc(100% - 60px)',
    backgroundColor: 'yellow',
    minWidth: 600,
    overflow: 'hidden',
    marginLeft: 60,
    marginTop: 40,
    marginBottom: 40,
    padding: 20,
    '& .MuiDataGrid-row.Mui-even:not(:hover)': {
      backgroundColor: theme.palette.grey[100]
    }
  },
  heading: {
    marginTop: 0,
    // marginLeft: 20,
  },
  projectsButtons: {
    // margin: '10px 10px',
    marginBottom: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  projectsContainer: {
    display: 'flex',
    backgroundColor: 'red',
    paddingBottom: 50
  },
  elementContainer: {
    display: 'flex',
    width: '100%',
    marginBottom: 80,
    height: 'calc(100% - 120px)',
    // height: 'calc(100% - 160px)',
    overflowX: 'hidden',
    overflowY: 'auto'
  },
  table: {
    overflow: 'auto',
    // height: "100%",
    minHeight: 600,
    marginBottom: 0,
    width: '49%',
    border: 'none',
    borderRight: `1px solid ${theme.palette.divider}`,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  details: {
    height: 'auto',
    marginBottom: 0,
    width: '49%',
    padding: 10,
    overflow: 'auto'
  },
  detailsButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 10,
    width: '100%',
    minWidth: 550,
  },
  detailsButton: {
    marginRight: 10,
    height: 28
  },
  detailsStatus: {
    display: 'flex',
    marginTop: 10,
    minWidth: 550,
  },
  detailsBody: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },
  cards: {
    display: 'flex',
    justifyContent: 'space-around',
    flexWrap: 'no-wrap',
    listStyle: 'none',
    margin: '20px 0',
    padding: 0,
    width: '100%'
  },
  card: {
    width: 160,
  },
  detailsNumber: {
    marginLeft: 0,
  },
  description: {
    marginLeft: 0,
    backgroundColor: theme.palette.grey[100],
    border: '1px solid lightgrey',
    borderRadius: 5,
    width: '100%',
    padding: 10
  },
  emptyProject: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  search: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    marginRight: 10,
    // marginLeft: 10,
    paddingLeft: 5,
    paddingRight: 5,
    height: 28,
    width: 400,
    display: 'flex',
    alignItems: 'center'
  },
  searchIcon: {
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  inputRoot: {
    color: 'inherit',
    width: '100%',
    display: 'flex'
  },
  inputInput: {
    paddingLeft: 24,
    fontSize: '12px'
  },
}));

export const ProjectsContainer = () => {

  // REDUX
  const user = useSelector(selectUser);
  const projects = useSelector(selectProjects);
  const currentProject = projects?.byId?.[projects?.currentProjectId];
  const lists = useSelector(selectLists);
  const wells = useSelector(selectWells);
  const typeCurves = useSelector(selectTypeCurves);
  const groupWells = useSelector(selectGroupWells);
  const economics = useSelector(selectEconomics);
  const fba = useSelector(selectFBA);
  const options = useSelector(selectOptions);
  const dispatch = useDispatch();

  // STYLES
  const classes = useStyles();
  const theme = useTheme();


  // LOCAL STATES

  const [editProjectVisible, setEditProjectVisible] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPublicProjects, setShowPublicProjects] = useState(false);
  // const [paginationModel, setPaginationModel] = useState({pageSize: 1, page: 0});


  const { enqueueSnackbar } = useSnackbar();

  const { socket } = useSocket();

  const darkMode = user?.value?.darkMode;
  const demoUser = user?.value?.subscription === 'Demo';

  function handleEditProject(newProject) {
    setEditProjectVisible(newProject ? true : projects.currentProjectId);
  };
  function handleEditProjectClose() {
    setEditProjectVisible(false);
  };
  function handleShowPublicProjectsChange() {
    const newShowPublicProjects = !showPublicProjects;
    if (!newShowPublicProjects) {
      !projects?.byId?.[projects?.allIds?.[0]]?.public && dispatch(makeProjectCurrent(projects?.allIds?.[0]));
    };
    setShowPublicProjects(newShowPublicProjects);
  };

  const columns = [
    {
      field: 'open',
      headerName: '',
      width: 125,
      editable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          size="small"
          style={{height: 28}}
          startIcon={<OpenIcon />}
          to={`/wells`}
          component={Link}
          onClick={e => handleLoadProject(e, projects?.byId?.[params?.id])}
        >
          Load
        </Button>
      ),
    },
    {
      field: 'projectName',
      headerName: 'Project Name',
      minWidth: 250,
      editable: false,
      flex: 1
    },
    {
      field: 'lastModified',
      headerName: 'Last Modified',
      width: 200
    },
    {
      field: 'currentProject',
      headerName: '',
      width: 50,
      editable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      style: {border: '1px solid red'},
      renderCell: (params) => (
        <IconButton
          variant="contained"
          color="primary"
          size="small"
        >
          {params.value === true && <ChevronIcon />}
        </IconButton>
      ),
    },
  ];

  try {
    let rows = [];
    const currentProjectId = projects.currentProjectId;
    const currentListId = lists.currentListId;

    let projectIds = [...projects.allIds] || [];
    if (searchName.length > 0) {
      projectIds = projects.allIds?.filter(id => projects.byId[id]?.name?.toLowerCase()?.includes(searchName?.toLowerCase())) || [];
    };
    if (!showPublicProjects) {
      projectIds = [...projectIds?.filter(id => !projects.byId[id]?.public || projects.byId[id]?.createdBy === user?.value?.email)] || [];
    };

    projectIds = projectIds?.length > 0 ? [...projectIds]?.sort((a, b) => new Date(projects.byId?.[b]?.lastModified) - new Date(projects.byId?.[a]?.lastModified)) : [];

    projectIds?.forEach(id => {
      rows.push({
        id: id,
        currentProject: id === currentProjectId,
        projectName: projects.byId[id]?.name,
        dateCreated: projects.byId[id]?.createdOn,
        lastModified: new Date(projects.byId[id]?.lastModified)?.toLocaleString(),
        listCount: projects.byId[id]?.listIds?.length || 0,
        wellCount: lists.byId[currentListId]?.wellIds?.length || 0,
      });
    });

    if (document.title !== ('Projects | SSD')) document.title = ('Projects | SSD');

    if (user?.value?.subscription === 'SSD' && !user?.value?.forecastAccess && !user?.value?.fbaAccess) {
      return (
        <div style={{width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
          <Typography>Please add subscription to your account</Typography>
        </div>
      );
    } else {
      return (
        <Paper className={classes.root} style={{backgroundColor: user?.value?.darkMode ? '#121212' : '#f5f5f5'}}>
          
          {editProjectVisible && <EditProject project={editProjectVisible && projects?.byId?.[editProjectVisible]} handleEditProjectClose={handleEditProjectClose} />}

          <Typography variant="h5" className={classes.heading} color="textSecondary">
            PROJECTS
          </Typography>


          <div className={classes.projectsButtons}>
            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
              <div className={classes.search} style={{border: `1px solid ${darkMode ? "#fff" : theme.palette.primary.main}`}}>
                <div className={classes.searchIcon}>
                  <Search fontSize="16px" color={darkMode ? "inherit" : "primary"} />
                </div>
                <InputBase
                  placeholder="Search projects by name"
                  classes={{
                    root: classes.inputRoot,
                    input: classes.inputInput,
                  }}
                  value={searchName}
                  onChange={e => setSearchName(e.target.value)}
                />
              </div>

              <FormGroup style={{marginTop: 0, marginLeft: 20}}>
                <FormControlLabel 
                  control={<Switch />} 
                  label={<Typography style={{fontSize: 12}}>Show Public Projects</Typography>}
                  size="small" 
                  checked={showPublicProjects} 
                  onChange={handleShowPublicProjectsChange}
                  disabled={user?.value?.email === 'wcap@ssdynamics.com'}
                />
              </FormGroup>
            </div>  

            <div>
              <Tooltip title="Import project">
                <span><Button
                  size="small"
                  component="label"
                  color="primary" 
                  variant="outlined" 
                  style={{marginRight: 10, marginLeft: 10, height: 28}} 
                  startIcon={<UploadIcon />}
                  disabled={demoUser}
                >
                  Import Project
                  <input
                    type="file"
                    accept=".json"
                    hidden
                    onChange={e => handleImportProject(e)}
                  />
                </Button></span>
              </Tooltip>
              <Tooltip title="New project">
                <span><Button 
                  size="small"
                  edge="end" 
                  color="primary" 
                  variant="contained" 
                  style={{height: 28}}
                  startIcon={<AddIcon />} 
                  onClick={e => handleEditProject(true)} disabled={demoUser}
                >
                  New Project
                </Button></span>
              </Tooltip>
            </div>
          </div>
          
          {/* <Divider /> */}


          <Paper className={classes.elementContainer}>
            <DataGrid
              sx={{
                '& .super-app-theme--Current': {
                  backgroundColor: 'rgba(7, 88, 183, 0.25)' // highlight the current row
                }
              }}
              density='compact'
              // paginationModel={paginationModel}
              // onPaginationModelChange={setPaginationModel}
              onCellClick={(params) => {
                dispatch(makeProjectCurrent(params.id));
                dispatch(makeListCurrent(projects?.byId?.[params.id]?.currentListId));
                dispatch(updateCurrentEconomics({id: projects?.byId?.[params.id]?.economicsIds?.[0] || ""}));
                dispatch(setCurrentFBAId(projects?.byId?.[params.id]?.fbaIds?.[0] || ""));
              }}
              rows={rows}
              columns={columns}
              // pagination
              // pageSize={5}
              // pageSizeOptions={[1, 5, 10, 25]}
              rowsPerPageOptions={[10, 25, 50, 100]}
              className={classes.table}
              editable={false}
              getRowClassName={(params) => {
                return (params.row.id === projects?.currentProjectId) ? `super-app-theme--Current` : '';
              }}
              disableSelectionOnClick
            />
            <div className={classes.details}>
              <div className={classes.detailsHeading}>
                <Typography variant="h6" color="textSecondary" style={{fontSize: 14}}>
                  PROJECT DETAILS
                </Typography>
                <Typography variant="h5" color="textPrimary" noWrap>
                  {projects.byId[currentProjectId]?.name || "Default Project"}
                </Typography>

                <div className={classes.detailsStatus}>
                  <Typography variant="body2" fontSize="12px" style={{display: 'flex', alignItems: 'center'}}>
                    <AccessTime style={{fontSize: 16, marginRight: 5}} /> Created: {(projects.byId[currentProjectId]?.createdOn ? new Date(projects.byId[currentProjectId]?.createdOn) : new Date())?.toLocaleString() || ""}
                  </Typography>
                  <Typography variant="body2" fontSize="12px" style={{display: 'flex', alignItems: 'center'}}>
                    <AccessTime style={{fontSize: 16, marginRight: 5, marginLeft: 20}} /> Modified: {(projects.byId[currentProjectId]?.lastModified ? new Date(projects.byId[currentProjectId]?.lastModified) : new Date())?.toLocaleString() || ""}
                  </Typography>
                  <Typography variant="body2" fontSize="12px" style={{display: 'flex', alignItems: 'center'}}>
                    <Group style={{fontSize: 16, marginRight: 5, marginLeft: 20}} /> Visibility: {projects.byId[currentProjectId]?.public ? 'Public' : 'Private'}
                  </Typography>
                </div>

                <div className={classes.detailsButtons}>
                  <Tooltip title="Delete project">
                    <span><Button 
                      size="small"
                      color="primary" 
                      variant="text" 
                      startIcon={<Delete/>} 
                      className={classes.detailsButton} 
                      onClick={() => setDeleteDialog(true)}
                      disabled={user?.value?.email !== projects?.byId[currentProjectId]?.createdBy}
                    >
                      Delete
                    </Button></span>
                  </Tooltip>
                  <Tooltip title="Duplicate project">
                    <Button 
                      size="small"
                      color="primary" 
                      variant="text" 
                      startIcon={<CopyAll />} 
                      className={classes.detailsButton} 
                      onClick={() => handleDuplicateProject(projects.byId[currentProjectId])}
                    >
                      Duplicate
                    </Button>
                  </Tooltip>
                  <Tooltip title="Edit project">
                    <Button size="small" color="primary" variant="text" startIcon={<Edit/>} className={classes.detailsButton} onClick={() => handleEditProject(false)}>
                      Edit
                    </Button>
                  </Tooltip>
                  <Tooltip title="Download project">
                    <span><Button 
                      size="small"
                      color="primary" 
                      variant="text" 
                      startIcon={<GetApp/>} 
                      className={classes.detailsButton} 
                      onClick={() => handleExportProject(projects.byId[currentProjectId], lists, wells, typeCurves, groupWells, economics, options)}
                      disabled={demoUser}
                    >
                      Download
                    </Button></span>
                  </Tooltip>
                  <div style={{position: 'relative'}}>
                    <Tooltip title="Save project">
                      <span><Button 
                        size="small"
                        color="primary" 
                        variant="text" 
                        startIcon={<CloudUpload/>} 
                        className={classes.detailsButton} 
                        onClick={() => handleSaveProject(projects.byId[currentProjectId])}
                        disabled={user?.value?.email !== projects?.byId[currentProjectId]?.createdBy || demoUser || isLoading}
                      >
                        Save
                      </Button></span>
                    </Tooltip>
                    {isLoading && (
                      <CircularProgress
                        size={24}
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          marginTop: '-11px',
                          marginLeft: '-12px',
                        }}
                      />
                    )}
                  </div>

                  <Button 
                    size="small"
                    color="primary" 
                    variant="contained" 
                    startIcon={<OpenIcon/>} 
                    className={classes.detailsButton} 
                    style={{justifySelf: 'flex-start'}}
                    to={`/forecast`}
                    component={Link}
                    onClick={(e) => handleLoadProject(e, projects.byId[currentProjectId])}
                  >
                    Load
                  </Button>
                </div>

                <Divider style={{marginTop: 10}} />

                

                <div className={classes.detailsBody}>
                  <div className={classes.cards}>
                    <Card className={classes.card}>
                      <CardContent>
                        <Typography variant="caption" color="textPrimary">Well Lists</Typography>
                        <Typography style={{textAlign: 'center'}} variant="h3" className={classes.detailsNumber}>{projects?.byId[currentProjectId]?.listIds?.length || 0}</Typography>
                      </CardContent>
                      <CardActions>
                        <Button size="small" endIcon={<Launch />} to={`/wells`} component={Link}>View All</Button>
                      </CardActions>
                    </Card>
                    <Card className={classes.card}>
                      <CardContent>
                        <Typography variant="caption" color="textPrimary">Wells</Typography>
                        <Typography style={{textAlign: 'center'}} variant="h3" className={classes.detailsNumber}>{getNumberWells(currentProjectId)}</Typography>
                      </CardContent>
                      <CardActions>
                        <Button size="small" endIcon={<Launch />} to={`/forecast`} component={Link}>View All</Button>
                      </CardActions>
                    </Card>
                    <Card className={classes.card}>
                      <CardContent>
                        <Typography variant="caption" color="textPrimary">Economics</Typography>
                        <Typography style={{textAlign: 'center'}} variant="h3" className={classes.detailsNumber}>{projects?.byId[currentProjectId]?.economicsIds?.length || 0}</Typography>
                      </CardContent>
                      <CardActions>
                        <Button size="small" endIcon={<Launch />} to={`/economics`} component={Link}>View All</Button>
                      </CardActions>
                    </Card>
                    <Card className={classes.card}>
                      <CardContent>
                        <Typography variant="caption" color="textPrimary">FBAs</Typography>
                        <Typography style={{textAlign: 'center'}} variant="h3" className={classes.detailsNumber}>{projects?.byId[currentProjectId]?.fbaIds?.length || 0}</Typography>
                      </CardContent>
                      <CardActions>
                        <Button size="small" endIcon={<Launch />} to={`/fba`} component={Link} disabled={!user?.value?.fbaAccess}>View All</Button>
                      </CardActions>
                    </Card>
                  
                  </div>

                  <div style={{display: 'flex', padding: 10, flexDirection: 'column', width: '100%'}}>
                    <Typography style={{fontSize: 12}} color="textSecondary">DESCRIPTION</Typography>
                    <Typography variant="body1" className={classes.description}>{projects.byId[currentProjectId]?.description || ""}</Typography>
                  </div>

                </div>

              </div>
            </div>
          </Paper>
          {deleteDialog && <DeleteDialog 
            projectId={currentProjectId} 
            projectName={projects.byId[currentProjectId]?.name} 
            handleDeleteDialogClose={() => setDeleteDialog(false)}
            economics={economics}
          />}
        </Paper>
      );
    };
  } catch (error) {
    console.log(error);
    return <></>;
  };

 
  
  // FUNCTIONS

  function handleSaveProject(project) {
    try {
      if (!project) return;

      console.log('Saving project ' + project?.name);

      setIsLoading(true);

      // save all project economics
      const economicsIds = economics?.allIds?.filter(id => economics?.byId?.[id]?.projectId === currentProject?.id); // only keep economicsIds that correspond to the current project
      if (economicsIds?.length > 0) {
        for (let economicsId of economicsIds) {
          if (economics?.byId?.[economicsId]?.forecastNet && economics?.byId?.[economicsId]?.cashflowNet && economics?.byId?.[economicsId]?.cashflowGross) {
            dispatch(saveEconomics(economics?.byId?.[economicsId]));
          };
        };
      };

      // save all project fbas
      const fbaIds = fba?.allIds?.filter(id => fba?.byId?.[id]?.projectId === currentProject?.id); // only keep economicsIds that correspond to the current project
      if (fbaIds?.length > 0 && socket && socket.readyState === 1) {
        for (let fbaId of fbaIds) {
          const currFBA = fba?.byId?.[fbaId];
          if (currFBA?.fbaData) {

            socket.send(JSON.stringify({
              messageType: 'saveFBA', 
              fba: currFBA, 
              fileName: currFBA?.id, 
              userEmail: user?.value?.email
            }));

          };
        };
      };

      let newWells = project?.wells?.length > 0 ? [...project.wells] : [];

      // compile a list of UWIs in the project
      let projectUwis = [];

      project.listIds.forEach(listId => {

        lists.byId[listId]?.wellUwis?.forEach(wellUwi => {
          projectUwis.push(wellUwi);
        });

      });

      for (let wellUwi of projectUwis) { // save local params
        try {
          if (wells?.byUwi?.[wellUwi]?.status === 'processed' && wells?.byUwi?.[wellUwi]?.params && !wells?.byUwi?.[wellUwi]?.local) { // if params are available, well is processed
            let wellParams = {...wells?.byUwi?.[wellUwi]?.params};
            const wellFluids = Object.keys(wellParams)?.filter(f => f !== 'cutoff');
    
            for (let fluid of wellFluids) {
              wellParams[fluid] = {...wellParams?.[fluid], outliers: []};
            };

            const wellIndex = newWells.map(w => w.uwi)?.indexOf(wellUwi);
            if (wellIndex === -1) { // if well hasnt been saved before, add it
              newWells.push({uwi: wellUwi, params: wellParams});
            } else { // update existing saved well with new params
              newWells.splice(wellIndex, 1, {uwi: wellUwi, params: wellParams});
            };
          };
          
        } catch (error) {
          console.log(error);
        };
      };

      const newProject = {
        id: project?.id,
        name: project?.name, 
        color: project?.color,
        description: project?.description || "",
        createdBy: project?.createdBy,
        lastModified: new Date().toLocaleString(),
        createdOn: project?.createdOn,
        listIds: project?.listIds || [],
        wells: newWells,
        currentListId: project?.currentListId || "",
        economicsIds: economicsIds?.length > 0 ? economicsIds : (project?.economicsIds || []),
        fbaIds: fbaIds?.length > 0 ? fbaIds : (project?.fbaIds || []),
        public: project?.public || false
      };

      const newLists = project?.listIds?.length > 0 ? project?.listIds?.map(listId => {
        const currentList = lists.byId[listId];
        return {
          id: listId,
          name: currentList?.name,
          createdBy: currentList?.createdBy,
          lastModified: new Date().toLocaleString(),
          color: currentList?.color,
          description: currentList?.description || "",
          wellUwis: currentList?.wellUwis.filter(uwi => !wells.byUwi[uwi]?.local),
          typeCurveId: currentList?.typeCurveId,
          groupWellId: currentList?.groupWellId,
          forecastListTab: currentList?.forecastListTab,
          filters: currentList?.filters
        };
      }) : [];

      dispatch(addProject({ project: newProject, lists: newLists, email: user?.value?.email })).then(() => {
        setIsLoading(false);
        enqueueSnackbar(`Project ${newProject?.name} saved successfully`, { variant: 'success' });
      });
    } catch (error) {
      setIsLoading(false);
      enqueueSnackbar(`Could not save project ${project?.name}`, { variant: 'error' });
      console.log(error);
    };
  };

  function handleDuplicateProject(project) {
    try {
      
      const newProjectId = uuid();

      const newProject = {
        id: newProjectId,
        name: `${project?.name} (copy)`, 
        color: project?.color,
        description: project?.description || "",
        createdBy: user?.value?.email,
        lastModified: new Date().toLocaleString(),
        createdOn: new Date().toLocaleString(),
        listIds: project?.listIds || [],
        wells: project?.wells || [],
        currentListId: project?.currentListId || "",
        economicsIds: [],
        fbaIds: project?.fbaIds || [],
        public: project?.public || false
      };

      // duplicate lists
      const newLists = project?.listIds?.length > 0 ? project?.listIds?.map(listId => {
        const currentList = lists.byId[listId];
        return {
          id: uuid(),
          name: currentList?.name,
          createdBy: user?.value?.email,
          lastModified: new Date().toLocaleString(),
          color: currentList?.color,
          description: currentList?.description || "",
          wellUwis: currentList?.wellUwis.filter(uwi => !wells.byUwi[uwi]?.local),
          typeCurveId: currentList?.typeCurveId,
          groupWellId: currentList?.groupWellId,
          forecastListTab: currentList?.forecastListTab,
          filters: currentList?.filters
        };
      }) : [];

      

      dispatch(addProject({ project: newProject, lists: newLists, email: user?.value?.email })).then(() => {
        setIsLoading(false);
        enqueueSnackbar(`Project ${project?.name} duplicated successfully`, { variant: 'success' });
      });
    } catch (error) {
      console.log(error);
    };
  };

  function handleLoadProject(e, project) {
    e.stopPropagation();

    try {
      console.log(`Loading project ${project?.name}...`);
      const listIds = project.listIds;
      dispatch(getLists({projectId: project.id, listIds: listIds})).then(res => {
        const lists = res.payload.lists;
        const color = colors[Math.floor(Math.random() * colors.length - 1)];
        lists?.forEach(list => {
          let wells = {};
          let uwis = [];
          list.wellUwis.forEach(uwi => {
            wells[uwi] = {
              uwi,
              formattedUwi: formatUwi(uwi),
              color,
              lastModified: new Date().toLocaleString(),
              status: 'idle'
            };
            uwis.push(uwi);
          });
          dispatch(addWells({ uwis: uwis, wells: wells, listId: list?.id }));
        });
        dispatch(makeProjectCurrent(project?.id));
        dispatch(makeListCurrent(project?.currentListId));
      });
    } catch (error) {
      
    };
  };
  
  function handleImportProject(e) { // import project from a .json file
    
    try {
      console.log('Importing project...');
      let files = e.target.files;
  
      if (!files.length > 0) {
        return false;
      };
      
      let reader = new FileReader();
      
      reader.readAsText(files[0]);
      
      reader.onload = (event) => {
        let result = JSON.parse(event.target.result);
        let newLists = {byId: {}, allIds: [], currentListId: undefined};
        let listIds = [];
        result?.lists?.allIds?.forEach(listId => {
          const currentList = result.lists?.byId[listId];
          const id = uuid();
          listIds.push(id);

          newLists.byId[id] = {
            id,
            name: currentList?.name,
            createdBy: user?.value?.email,
            lastModified: currentList?.lastModified,
            color: currentList?.color,
            description: currentList?.description,
            wellUwis: currentList?.wellUwis,
            typeCurveId: currentList?.typeCurveId,
            groupWellId: currentList?.groupWellId,
            forecastListTab: currentList?.forecastListTab,
            filters: {}
          };
          newLists.allIds.push(id);
          newLists.currentListId = id;
        });

        const newEconomicsIds = [];
        const newProjectId = uuid();

        for (const econ of result?.economics) {
          const newEconId = uuid();
          dispatch(updateEconomics({ economics: {...econ, id: newEconId, projectId: newProjectId}, projectId: newProjectId, consolidation: econ?.settings?.consolidation }));
          newEconomicsIds.push(newEconId);
        };

        dispatch(importProject({ project: {...result.project, economicsIds: newEconomicsIds}, email: user?.value?.email, id: newProjectId, listIds }));
        dispatch(setLists({ lists: newLists }));
        dispatch(setWells({ wells: result.wells }));
        dispatch(setOptions({options: {...result?.options, processJobs: undefined, typeCurveJob: undefined, groupWellJob: undefined, localWellJob: undefined}}));
        
      };

      e.target.value = '';
      
    } catch (error) {
      console.log('Coudld not import project!');
      console.log(error);
    };
  };

  function handleExportProject(project, lists, wells, typeCurves, groupWells, economics, options) {
    try {
      let projectLists = {byId: {}, allIds: []};
      project?.listIds?.forEach(listId => {
        projectLists.byId[listId] = lists.byId[listId];
        projectLists.allIds.push(listId);
        projectLists.currentListId = listId;
      });
      
      let wellUwis = [];
      projectLists.allIds?.forEach(listId => {
        projectLists.byId[listId]?.wellUwis.forEach(wellUwi => {
          wellUwis.push(wellUwi);
        });
      });

      let projectWells = {byUwi: {}, allUwis: []};
      wellUwis.forEach(wellUwi => {
        projectWells.byUwi[wellUwi] = wells.byUwi[wellUwi];
        projectWells.allUwis.push(wellUwi);
      });

      let projectEconomics = [];
      project?.economicsIds?.forEach(economicsId => {
        projectEconomics.push(economics?.byId?.[economicsId]);
      });

      let jsonData = {
        project: project,
        lists: projectLists,
        wells: projectWells,
        typeCurves: typeCurves,
        groupWells: groupWells,
        economics: projectEconomics,
        options: options
      };

      let dataStr = JSON.stringify(jsonData);
      if (dataStr?.length > 0) {
        let dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
        const date = new Date().toLocaleDateString();
        let exportFileDefaultName = `${project.name}_${date}.json`;
        let linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();
      };
    } catch (error) {
      console.log('Could not export project!');
      console.log(error);
    };
  };

  function getNumberWells(projectId) {
    let wells = 0;
    projects.byId[projectId]?.listIds?.forEach(id => {
      wells += lists.byId[id]?.wellUwis?.length || 0;
    })
    return wells;
  };

};

function DeleteDialog({ projectId, projectName, handleDeleteDialogClose, economics }) {

  const dispatch = useDispatch();

  return (
    <ClickAwayListener onClickAway={handleDeleteDialogClose}>
      <Dialog open>
        <DialogTitle>
          <Typography>Delete Project?</Typography>
        </DialogTitle>
        <DialogContent>
          {`You are about to delete project ${projectName}. This action cannot be undone. Do you wish to continue?`}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} color="primary">Cancel</Button>
          <Button onClick={() => handleDeleteProject(projectId)} color="secondary">Delete</Button>
        </DialogActions>
      </Dialog>
    </ClickAwayListener>
  );

  function handleDeleteProject(projectId) {
    dispatch(deleteProject({projectId: projectId}));

    // save all project economics
    const economicsIds = economics?.allIds?.filter(id => economics?.byId?.[id]?.projectId === projectId); // only keep economicsIds that correspond to the current project
    if (economicsIds?.length > 0) {
      for (let economicsId of economicsIds) {
        dispatch(deleteEconomics(economicsId));
      };
    };
    handleDeleteDialogClose();
  };
};